import type { ComponentProps } from 'react';
import { FieldInfo, useFieldContext } from './shared';
import { Label } from '../label';
import { Input } from '../input';
import { cn } from '../../lib/utils';

export default function NumberField({
	label,
	className,
	inputClassName,
	description,
	placeholder,
	...props
}: ComponentProps<'input'> & { label: string; inputClassName?: string; description?: string }) {
	const field = useFieldContext<number | null | undefined>();

	// Use the provided placeholder or empty string. When a placeholder is provided,
	// the label should always be floated up to avoid overlap.
	const inputPlaceholder = placeholder ?? '';

	return (
		<div className={className}>
			<div className="text-muted-foreground relative">
				{label && (
					<Label
						data-slot="form-label"
						data-error={field.state.meta.isTouched && !field.state.meta.isValid}
						className={cn(
							'pointer-events-none absolute w-full truncate px-3.5 py-[17px] origin-top-left',
							'data-[error=true]:text-destructive',
							// When placeholder is provided, keep label statically floated without animation
							placeholder
								? 'translate-y-[-6px] scale-[0.85] transition-none'
								: 'floating transition-[transform] duration-300',
						)}
						htmlFor={field.name}
					>
						{label}
					</Label>
				)}
				<Input
					placeholder={inputPlaceholder}
					type="number"
					id={field.name}
					name={field.name}
					value={field.state.value ?? ''}
					onBlur={field.handleBlur}
					onChange={(e) => field.handleChange(e.target.value === '' ? null : Number(e.target.value))}
					className={cn('text-foreground h-12 px-3 pb-0 pt-4', inputClassName)}
					{...props}
				/>
			</div>
			{description && (
				<p data-slot="form-description" className="text-muted-foreground m-1 text-sm">
					{description}
				</p>
			)}
			<FieldInfo fieldState={field.state} />
		</div>
	);
}
